import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Snackbar, Alert, Typography, Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../api';
import { useNavigate } from "react-router-dom";
import PageHeader from './PageHeader';

export default function ActivitesTable({ onSelectActivite }) {
  const theme = useTheme();
  const custom = theme.palette.custom || {};

  const [activites, setActivites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: '' });
  const [editId, setEditId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchActivites = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/activites');
      setActivites(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors du chargement des activités.");
      setActivites([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchActivites();
  }, []);

  const handleOpen = (a = null) => {
    setOpen(true);
    setForm({ nom: a?.nom || '' });
    setEditId(a?._id || null);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ nom: '' });
    setEditId(null);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    if (!form.nom) {
      setSnack({ open: true, message: "Nom obligatoire", severity: 'error' });
      setLoading(false);
      return;
    }
    try {
      if (editId) {
        await api.put(`/activites/${editId}`, { nom: form.nom });
        setSnack({ open: true, message: "Activité modifiée", severity: 'success' });
      } else {
        await api.post('/activites', { nom: form.nom });
        setSnack({ open: true, message: "Activité ajoutée", severity: 'success' });
      }
      handleClose();
      fetchActivites();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || "Erreur", severity: 'error' });
    }
    setLoading(false);
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer cette activité ?')) return;
    setLoading(true);
    try {
      await api.delete(`/activites/${id}`);
      setSnack({ open: true, message: "Activité supprimée", severity: 'success' });
      fetchActivites();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || "Erreur lors de la suppression", severity: 'error' });
    }
    setLoading(false);
  };

  const handleSelect = (id) => {
    if (onSelectActivite) onSelectActivite(id);
  };

  if (loading) return <Typography sx={{ mt: 6, textAlign: 'center' }}>Chargement des activités...</Typography>;
  if (error) return (
    <Alert severity="error" sx={{ m: 4 }}>
      {error}
    </Alert>
  );
  if (!activites || !Array.isArray(activites)) return (
    <Alert severity="error" sx={{ m: 4 }}>
      Problème de données reçues du serveur.
    </Alert>
  );

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <PageHeader
        title="Activités & compétences"
        subtitle="Crée les activités sportives et accède rapidement aux compétences associées."
        backTo="/prof"
        homeTo="/"
        actionLabel="Ajouter une activité"
        onAction={() => handleOpen()}
      />

      <TableContainer component={Paper} sx={{ mt: 2, backgroundColor: custom.surfaceLight }}>
        <Typography variant="h6" sx={{ m: 2 }}>Gestion des activités sportives</Typography>

        {activites.length === 0 ? (
          <Typography sx={{ m: 4, textAlign: 'center', color: 'text.secondary' }}>
            Aucune activité créée.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: custom.headerBg }}>
                <TableCell>Nom</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Compétences</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activites.map(a => (
                <TableRow key={a._id} hover sx={{ '&:hover': { bgcolor: custom.rowHover } }}>
                  <TableCell onClick={() => handleSelect(a._id)} sx={{ cursor: 'pointer' }}>
                    {a.nom}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpen(a)}>Modifier</Button>
                    <Button size="small" color="error" onClick={() => handleDelete(a._id)}>Supprimer</Button>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/activites/${a._id}/competences`)}>
                      Voir les compétences
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{editId ? "Modifier l'activité" : "Ajouter une activité"}</DialogTitle>
          <DialogContent>
            <form id="activiteForm" onSubmit={handleSubmit}>
              <TextField
                label="Nom"
                name="nom"
                value={form.nom}
                onChange={handleChange}
                margin="normal"
                fullWidth
                required
              />
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Annuler</Button>
            <Button
              type="submit"
              form="activiteForm"
              variant="contained"
              sx={{ backgroundColor: custom.primaryBg, color: custom.primaryColor, '&:hover': { backgroundColor: '#dccdf0' } }}
            >
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        </Snackbar>
      </TableContainer>
    </Box>
  );
}